import type { DefaultError, QueryKey } from '@tanstack/query-core';
import { UseSuspenseInfiniteQueryOptions, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { FetchTasksParams, fetchTaskHistory } from 'api/tasks';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteScroll } from 'customHooks/useInfiniteScroll';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Task, TaskHistoryFieldUpdate, TaskHistoryItem } from 'types/domain';
import { PaginatedData } from 'types/pagination';

const DEFAULT_PAGE_LIMIT = 20;

type Options = Omit<
    UseSuspenseInfiniteQueryOptions<
        PaginatedData<TaskHistoryItem>,
        DefaultError,
        Task,
        PaginatedData<TaskHistoryItem>,
        QueryKey,
        number
    >,
    'queryKey' | 'initialPageParam' | 'select' | 'getNextPageParam'
>;

const useInfiniteTaskHistory = (
    taskId: number,
    params: Omit<FetchTasksParams, 'page'> = {},
    options: Options = {} as Options,
) => {
    const { limit = DEFAULT_PAGE_LIMIT, ...rest } = params;
    const nextParams = { limit, ...rest };
    const { accountId } = useCurrentAccount();
    const infiniteQueryData = useSuspenseInfiniteQuery({
        initialPageParam: 1,
        queryKey: accountQueryKeys.taskHistory(accountId, taskId, params),
        queryFn: ({ pageParam }) =>
            fetchTaskHistory(accountId, taskId, { page: pageParam, ...nextParams }),
        getNextPageParam: lastPage =>
            lastPage.meta.current_page < lastPage.meta.last_page
                ? lastPage.meta.current_page + 1
                : undefined,
        refetchOnWindowFocus: false,
        select: data => transformHistoryItems(data.pages.flatMap(p => p.data)),
        ...options,
    });
    const ref = useInfiniteScroll(infiniteQueryData);

    return { ...infiniteQueryData, ref };
};

const transformHistoryItems = (items: TaskHistoryItem[]) => {
    const transformedArr: TaskHistoryItem[] = [];
    for (const item of items) {
        if (item.event === 'updated') {
            Object.keys(item.new_values).forEach(key =>
                transformedArr.push({
                    ...item,
                    field: key,
                    new_values: {
                        [key]: (item.new_values as TaskHistoryFieldUpdate)[key],
                    },
                    old_values: {
                        [key]: (item.old_values as TaskHistoryFieldUpdate)[key],
                    },
                }),
            );
        } else {
            transformedArr.push(item);
        }
    }

    return transformedArr;
};

export { useInfiniteTaskHistory };
